/**
 * The coffee module's frontend manifest — everything it adds to the interface,
 * in one place. The backend half is `backend/src/modules/coffee/`.
 *
 * **Everything it renders sits inside an `ErrorBoundary`.** The module is for
 * fun and must never cost anybody a working screen: a crash in its dashboard
 * card renders nothing there, and a crash on its pages renders a one-line
 * apology inside the shell, with the menu still working.
 *
 * The accounts page lives in a **Settings** section the module declares
 * itself, as currency does; both land in one section.
 */
import { useTranslation } from 'react-i18next';
import CoffeeIcon from '@mui/icons-material/Coffee';
import CoffeeMakerOutlinedIcon from '@mui/icons-material/CoffeeMakerOutlined';
import Alert from '@mui/material/Alert';
import { COFFEE_PERMISSIONS } from '@forsys/shared';
import { ErrorBoundary } from '../../components/ErrorBoundary';
import { defineDashboardCard, defineFrontendModule } from '../types';
import AccountsPage from './AccountsPage';
import { DashboardCoffee, machineOptions, type CoffeeCardConfig } from './DashboardCoffee';
import MachinesPage from './MachinesPage';

function Crashed() {
  const { t } = useTranslation('coffee');
  return <Alert severity="error">{t('crashed')}</Alert>;
}

function SafeDashboardCoffee(props: { readonly config: CoffeeCardConfig }) {
  return (
    <ErrorBoundary>
      <DashboardCoffee {...props} />
    </ErrorBoundary>
  );
}

const machineCard = defineDashboardCard<CoffeeCardConfig>({
  id: 'machine',
  titleKey: 'coffee:dashboard.title',
  icon: CoffeeIcon,
  anyOf: [COFFEE_PERMISSIONS.USE],
  component: SafeDashboardCoffee,
  width: 1,
  minWidth: 1,
  maxWidth: 2,
  // After core's statistics, so that it fills the place they leave in their row.
  order: 150,
  defaultConfig: { machine: 'all' },
  settings: [{ kind: 'select', key: 'machine', labelKey: 'coffee:dashboard.machine', options: machineOptions }],
  // One card with every machine; a card for one machine is added by hand.
  defaults: () => [{ key: 'all', config: { machine: 'all' } }],
});

export const coffeeModule = defineFrontendModule({
  key: 'coffee',
  sections: [{ id: 'settings', titleKey: 'coffee:nav.section.settings', order: 900 }],
  pages: [
    {
      path: 'coffee',
      element: (
        <ErrorBoundary fallback={<Crashed />}>
          <MachinesPage />
        </ErrorBoundary>
      ),
      anyOf: [COFFEE_PERMISSIONS.USE],
      // Exact, or it would also claim `/coffee/accounts` — both entries lit, and the header naming Overview.
      nav: { section: 'overview', labelKey: 'coffee:nav.machines', icon: CoffeeIcon, exact: true },
    },
    {
      path: 'coffee/accounts',
      element: (
        <ErrorBoundary fallback={<Crashed />}>
          <AccountsPage />
        </ErrorBoundary>
      ),
      anyOf: [COFFEE_PERMISSIONS.MANAGE],
      nav: { section: 'settings', labelKey: 'coffee:nav.accounts', icon: CoffeeMakerOutlinedIcon },
    },
  ],
  dashboardCards: [machineCard],
});
